import type { DemoKey } from '../types';

export const en: Record<DemoKey, string> = {
  'app.eyebrow': 'Math · Statistics',
  'app.title': 'Statistics',
  'panel.experiment': 'Scene',
  'panel.params': 'Parameters',

  'exp.survey-explorer.title': 'Survey explorer',
  'exp.survey-explorer.desc': 'Pick a column; the demo picks the right summary.',
  'exp.distribution-sculptor.title': 'Distribution sculptor',
  'exp.distribution-sculptor.desc': 'Drag points to see how stats respond.',
  'exp.two-variable-lab.title': 'Two-variable lab',
  'exp.two-variable-lab.desc': 'Scatter, fit, ask whether one causes the other.',
  'exp.sampling-lab.title': 'Sampling lab',
  'exp.sampling-lab.desc': 'How sampling technique and size move the sample mean.',
  'exp.probability-diagrams.title': 'Probability diagrams',
  'exp.probability-diagrams.desc': 'Venn and tree views of compound events.',

  'type.qualitative-nominal.label': 'Qualitative · nominal',
  'type.qualitative-nominal.def': 'Categories with no inherent order.',
  'type.qualitative-ordinal.label': 'Qualitative · ordinal',
  'type.qualitative-ordinal.def': 'Categories with a meaningful order.',
  'type.qualitative-multi.label': 'Qualitative · multi-valued',
  'type.qualitative-multi.def': 'Each observation can have multiple tags.',
  'type.quantitative-discrete.label': 'Quantitative · discrete',
  'type.quantitative-discrete.def': 'Numbers that come in fixed steps.',
  'type.quantitative-continuous.label': 'Quantitative · continuous',
  'type.quantitative-continuous.def': 'Numbers measured on a continuous scale.',

  'column.name.label': 'Student name',
  'column.height_cm.label': 'Height',
  'column.shoe_size_eu.label': 'Shoe size (EU)',
  'column.study_hours.label': 'Study hours / week',
  'column.test_score.label': 'Test score',
  'column.sports_played.label': 'Sports played',
  'column.eye_color.label': 'Eye colour',
  'column.commute_min.label': 'Commute',
  'column.grade_year.label': 'Year group',
  'column.homework_effort.label': 'Homework effort',
  'column.ice-cream-sales.label': 'Ice-cream sales',
  'column.drownings.label': 'Drownings',
  'column.month.label': 'Month',
  'column.storks.label': 'Storks',
  'column.births.label': 'Births',
  'column.country_area_km2.label': 'Country area',
  'column.sleep_hours.label': 'Sleep hours',
  'column.reaction_ms.label': 'Reaction time',

  'dataset.master-survey.label': 'Class survey',
  'dataset.master-survey.desc': '30 simulated students.',
  'dataset.spurious-ice-cream-vs-drownings.label': 'Ice-cream sales vs drownings',
  'dataset.spurious-ice-cream-vs-drownings.desc':
    'Both rise and fall with the season. Drownings don’t cause ice-cream sales.',
  'dataset.spurious-storks-vs-births.label': 'Storks vs births',
  'dataset.spurious-storks-vs-births.desc':
    'Bigger countries have more storks and more births. Birds aren’t the cause.',
  'dataset.real-sleep-vs-reaction-time.label': 'Sleep vs reaction time',
  'dataset.real-sleep-vs-reaction-time.desc':
    'More sleep, faster reactions. A real negative correlation — and a plausible mechanism.',

  'tag.football.label': 'Football',
  'tag.basketball.label': 'Basketball',
  'tag.volleyball.label': 'Volleyball',
  'tag.swimming.label': 'Swimming',
  'tag.none.label': 'None',

  'population.gaussian.label': 'Bell curve',
  'population.right-skewed.label': 'Right-skewed',
  'population.bimodal.label': 'Two-peak (with strata)',
  'population.uniform.label': 'Flat',

  'technique.simple-random.label': 'Simple random',
  'technique.stratified.label': 'Stratified',
  'technique.systematic.label': 'Systematic',
  'technique.biased-convenience.label': 'Biased convenience',

  'shape.symmetric': 'Symmetric',
  'shape.right-skewed': 'Right-skewed',
  'shape.bimodal': 'Bimodal',
  'shape.with-outliers': 'With outlier',
  'shape.empty': 'Empty',

  'venn.dice-even-or-prime.label': 'Dice: even or prime',
  'venn.class-sports-2set.label': 'Class sports (2 sets)',
  'venn.class-sports-3set.label': 'Class sports (3 sets)',
  'venn.custom.label': 'Custom',
  'venn.set.even': 'Even',
  'venn.set.prime': 'Prime',

  'tree.coin-toss.label': 'Coin toss',
  'tree.dice-roll.label': 'Dice roll',
  'tree.draw-without-replacement.label': 'Draw without replacement',
  'tree.custom.label': 'Custom',
  'tree.coin.start': 'Start',
  'tree.coin.heads': 'H',
  'tree.coin.tails': 'T',
  'tree.dice.start': 'Roll',
  'tree.die.1': '1',
  'tree.die.2': '2',
  'tree.die.3': '3',
  'tree.die.4': '4',
  'tree.die.5': '5',
  'tree.die.6': '6',
  'tree.cond.met': '✓',
  'tree.cond.not': '✗',
  'tree.draw.start': 'Bag: 2 red, 3 blue',
  'tree.draw.red': 'Red',
  'tree.draw.blue': 'Blue',
  'tree.custom.start': 'Start',
  'tree.custom.A': 'A',
  'tree.custom.B': 'B',
  'tree.custom.C': 'C',
  'tree.custom.D': 'D',
  'tree.custom.E': 'E',
  'tree.legend.custom': 'click a probability to edit it — the change applies at every level',

  'cond.even': 'Each die even',
  'cond.odd': 'Each die odd',
  'cond.lt4': 'Each die < 4',
  'cond.ge5': 'Each die ≥ 5',
  'cond.sum-lt8': 'Sum < 8',
  'cond.sum-eq7': 'Sum = 7',
  'cond.doubles': 'Doubles',

  'card.histogram': 'Histogram',
  'card.frequency': 'Frequency chart',
  'card.cumulative': 'Cumulative frequency',
  'card.box': 'Box-and-whisker',
  'card.summary': '5-number summary',
  'card.bar': 'Bar chart',
  'card.mode': 'Mode',
  'card.tag-frequency': 'Tag frequency',
  'card.mode-tag': 'Top tag',
  'card.empty-no-order': 'Not applicable',
  'card.empty-multi': 'Not applicable',
  'card.numberline': 'Number line',
  'card.scatter': 'Scatter',
  'card.description': 'What’s going on',
  'card.population': 'Population',
  'card.sampling-distribution': 'Sample means',
  'card.venn': 'Venn',
  'card.tree': 'Tree',
  'card.probabilities': 'Probabilities',

  'param.dataset.label': 'Dataset',
  'param.column.label': 'Column',
  'param.bins.label': 'Bins',
  'param.shape.label': 'Starting shape',
  'param.show-mean.label': 'Show mean',
  'param.show-median.label': 'Show median',
  'param.x-col.label': 'X column',
  'param.y-col.label': 'Y column',
  'param.show-regression.label': 'Regression line',
  'param.show-residuals.label': 'Residuals',
  'param.show-confounder.label': 'Show confounder',
  'param.population.label': 'Population',
  'param.technique.label': 'Technique',
  'param.sample-size.label': 'Sample size',
  'param.stratum.label': 'Stratum',
  'param.bias.label': 'Bias',
  'param.mode.label': 'Mode',
  'param.preset.label': 'Preset',
  'param.universe.label': 'Universe size',
  'param.show-prob.label': 'Show probabilities',
  'param.treat-as-numeric.label': 'Treat as numeric',
  'param.tosses.label': 'Coin tosses',
  'param.dice.label': 'Dice',
  'param.condition.label': 'Condition',
  'param.events.label': 'Events',
  'param.levels.label': 'Levels',

  'mode.venn': 'Venn',
  'mode.tree': 'Tree',

  'count.2': '2 events',
  'count.3': '3 events',
  'count.4': '4 events',
  'count.5': '5 events',

  'stat.count': 'Count',
  'stat.mean': 'Mean',
  'stat.median': 'Median',
  'stat.range': 'Range',
  'stat.min': 'Min',
  'stat.max': 'Max',
  'stat.q1': 'Q1',
  'stat.q3': 'Q3',
  'stat.iqr': 'IQR',
  'stat.outliers': 'Outliers',
  'stat.r': 'Pearson r',
  'stat.r2': 'r²',
  'stat.slope': 'Slope',
  'stat.intercept': 'Intercept',
  'stat.n': 'n',
  'stat.population-mean': 'True mean',
  'stat.latest-sample-mean': 'Sample mean',
  'stat.sample-mean-spread': 'Sample-mean SD',
  'stat.sample-count': 'Samples drawn',
  'stat.sd': 'SD',
  'stat.mode': 'Mode',
  'stat.distinct': 'Distinct values',
  'outlier.upper': 'Upper outliers',
  'outlier.lower': 'Lower outliers',

  'table.data': 'Data',
  'table.value': 'Value',
  'table.sample': 'Sample',
  'table.element': 'Element',

  'region.A': 'A',
  'region.B': 'B',

  'btn.resample': 'Resample',
  'btn.run-100': 'Run 100 samples',
  'btn.stop': 'Stop',
  'btn.clear-history': 'Clear history',

  'legend.points': '⊕ click · ⊖ right-click',

  'explainer.no-order': 'No order → no cumulative, no box plot.',
  'explainer.multi-no-iqr': 'Multi-valued → quartile / IQR not defined.',
  'message.no-data': 'No data.',
  'message.needs-quartiles': 'Needs ≥4 points for quartiles.',
  'message.x-zero-variance': 'X has zero variance — no line to fit.',
  'message.n-too-small': 'n too small.',
  'message.same-columns': 'Choose different columns.',
  'message.stratum-clamp': 'Stratum had fewer rows than requested — used all available.',
  'message.siblings-sum-to-one': 'Siblings must sum to 1.',
  'message.add-points': 'Add points to the number line.',
  'message.no-strata': 'This population has no strata.',
};
